// Tenant branding consumer — single source of truth for the UI.
//
// Reads the backend's /api/v1/tenant/branding once at app start (the
// query hook caches it with staleTime: Infinity), and falls back to the
// pre-refactor hard-coded ICS-TEST-01 values while in flight or on
// failure. The fallbacks are the *current* production values, so a
// network blip during boot leaves the UX identical to today rather than
// rendering an empty title or no footer.
//
// Layer 3d adds `applyBrandingColors()`: pushes the three tenant-driven
// colors into the CSS variables defined in global.css. Tailwind reads
// those vars via `rgb(var(--color-X) / <alpha-value>)`, so a single
// runtime mutation re-themes every component that uses `bg-rame`,
// `text-ink`, `bg-paper`, etc.

import { useEffect } from "react";

import { useTenantBrandingQuery } from "@/api/queries";

interface Branding {
  displayName: string;
  assistantName: string;
  pageTitleSuffix: string;
  legalFooterText: string;
  primaryColor: string;
  brandPriorityActive: boolean;
  brandDisclosureText: string | null;
}

const FALLBACK: Branding = {
  displayName: "InCorpoSano",
  assistantName: "MIMIR",
  pageTitleSuffix: " · InCorpoSano",
  legalFooterText: "InCorpoSano S.r.l. · P.IVA IT 0123456789",
  primaryColor: "#B8623A",
  // ICS-TEST-01 doesn't privilege any brand. Fallback stays neutral so
  // a network blip during boot doesn't surface a disclosure that
  // shouldn't be there for the default tenant.
  brandPriorityActive: false,
  brandDisclosureText: null,
};

export function useBranding(): Branding {
  const { data } = useTenantBrandingQuery();
  if (!data) return FALLBACK;
  return {
    displayName: data.display_name,
    assistantName: data.assistant_name,
    pageTitleSuffix: data.page_title_suffix,
    legalFooterText: data.legal_footer_text,
    primaryColor: data.primary_color,
    brandPriorityActive: data.brand_priority_active,
    brandDisclosureText: data.brand_disclosure_text,
  };
}


// ─── Color application at boot ──────────────────────────────────────
//
// The backend serves colors as hex strings (#B8623A). Tailwind's CSS
// variable pattern needs an RGB tuple (`184 98 58`) so alpha modifiers
// can compose. `hexToRgbTuple` does the conversion; the hook below
// applies the three tenant-driven slots to `:root` once the fetch
// resolves.

function hexToRgbTuple(hex: string): string | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  // m[1] is defined by construction when m matches — the explicit check
  // is for `noUncheckedIndexedAccess` strict mode.
  const digits = m?.[1];
  if (!digits) return null;
  const n = parseInt(digits, 16);
  return `${(n >> 16) & 0xff} ${(n >> 8) & 0xff} ${n & 0xff}`;
}

/** Push the branding colors into `:root` so Tailwind utility classes
 * (`bg-rame`, `text-ink`, `bg-paper`) reflect the resolved tenant.
 * No-op when the fetch hasn't resolved yet — global.css carries the
 * ICS defaults, so the page renders correctly during the fetch window. */
export function useApplyBrandingColors(): void {
  const { data } = useTenantBrandingQuery();
  useEffect(() => {
    if (!data) return;
    const root = document.documentElement;
    const slots: [string, string | null | undefined][] = [
      ["--color-rame", data.primary_color],
      ["--color-paper", data.paper_color],
      ["--color-ink", data.ink_color],
    ];
    for (const [name, hex] of slots) {
      if (!hex) continue;
      const tuple = hexToRgbTuple(hex);
      // Invalid hex → leave the CSS default in place rather than render
      // a broken color. The backend is the source of truth for what
      // a valid color string looks like; we don't second-guess shape here.
      if (tuple !== null) root.style.setProperty(name, tuple);
    }
  }, [data]);
}

// ─── PWA branding at runtime ──────────────────────────────────────────
//
// The static manifest.webmanifest baked at build time carries the
// default ICS-TEST-01 colors/name. Once the tenant branding query
// resolves, this hook swaps in a dynamic manifest blob so that
// "Add to Home Screen" picks up the correct tenant identity. It also
// updates <meta name="theme-color"> so the OS chrome matches.
//
// The previous blob URL is revoked to avoid leaking memory on re-renders.

/** Replace the static PWA manifest and theme-color with live tenant
 *  branding data as soon as the /api/v1/tenant/branding fetch resolves. */
export function useApplyPWABranding(): void {
  const { data } = useTenantBrandingQuery();
  useEffect(() => {
    if (!data) return;

    const tenantFolder = window.location.hostname.includes("ldf-poc") ? "ldf" : "ics";

    // 1. Update <meta name="theme-color">
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeMeta) {
      themeMeta.content = data.primary_color;
    }

    // 2. Build a dynamic manifest and swap <link rel="manifest">
    const manifestObj = {
      name: data.display_name,
      short_name: data.display_name,
      theme_color: data.primary_color,
      display: "standalone",
      start_url: "/",
      scope: "/",
      background_color: "#FAF8F4",
      orientation: "portrait",
      categories: ["health", "lifestyle"],
      icons: [
        { src: `/icons/${tenantFolder}/icon-192.png`, sizes: "192x192", type: "image/png" },
        { src: `/icons/${tenantFolder}/icon-512.png`, sizes: "512x512", type: "image/png" },
        { src: `/icons/${tenantFolder}/icon-512-maskable.png`, sizes: "512x512", type: "image/png", purpose: "maskable" },
      ],
    };

    const blob = new Blob([JSON.stringify(manifestObj)], { type: "application/manifest+json" });
    const newUrl = URL.createObjectURL(blob);

    const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (manifestLink) {
      const prevHref = manifestLink.href;
      manifestLink.href = newUrl;
      // Revoke the previous blob URL to avoid memory leaks on re-renders.
      // Leave static /manifest.webmanifest alone — only revoke blob: URLs.
      if (prevHref.startsWith("blob:")) {
        URL.revokeObjectURL(prevHref);
      }
    }

    // 3. Swap <link rel="apple-touch-icon"> for the current tenant.
    let appleIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (!appleIcon) {
      appleIcon = document.createElement("link");
      appleIcon.rel = "apple-touch-icon";
      document.head.appendChild(appleIcon);
    }
    appleIcon.href = `/icons/${tenantFolder}/apple-touch-icon-180.png`;
  }, [data]);
}
